/**
 * The one-line stat preview a type-ahead suggestion renders beside its name
 * (issue 190): "Evocation · 1 standard action", "CR 5 · Dragon",
 * "Wondrous Item · 2,500 gp".
 *
 * WHICH fields preview is the server's derive rule
 * (server/utils/searchPreview.js — the Family's own show_in_list columns);
 * the payload arrives as an ordered `preview: [{key, value}]` of RAW authored
 * values, and this module only decides how a value is SPELLED — by reusing the
 * renderer's own seams rather than growing a second opinion:
 *
 *   · a number goes through `formatScalar`, so the authored `format`, `unit`,
 *     and `prefix` apply exactly as they do on the list card and the detail
 *     page. That is also what keeps a price in its AUTHORED denomination — a
 *     Traveller ship authored 37.08 at unit MCr previews "37.08 MCr", never
 *     the register's normalized 37,080,000;
 *   · a bare number (no unit, no prefix) is prefixed by its badge's own
 *     authored `label_prefix` when it has one ("CR 5"), else by its label
 *     ("Level 1") — the spelling the card badge already uses;
 *   · a boolean renders its label when true ("Prestige Class") and nothing
 *     otherwise;
 *   · a reference arrives as its target name(s) and renders as text.
 *
 * A Family the bundle has never seen (a user-authored system whose registry
 * payload has not been installed) degrades to plain strings and numbers —
 * booleans and objects are skipped, because without the declaration there is
 * no honest way to spell them.
 */

import formatScalar from '../components/generic/formatValue';
import { getFamily } from '../config/familyRegistry';
import { getActiveSystem } from '../config/system';

function partText(attr, value) {
  if (Array.isArray(value)) {
    return value
      .filter((v) => v !== null && v !== undefined && v !== '')
      .map(String)
      .join(', ');
  }
  if (!attr) {
    // Unknown Family: only self-describing scalars are honest.
    if (typeof value === 'boolean' || (value !== null && typeof value === 'object')) return '';
    return value === null || value === undefined ? '' : String(value);
  }
  if (attr.datatype === 'boolean') return value === true ? attr.label : '';
  if (attr.kind === 'reference' || attr.kind === 'reverse') return String(value);
  const text = formatScalar(attr, value);
  if (!text) return '';
  if (attr.datatype === 'number' && !attr.unit && !attr.prefix) {
    const labelPrefix = (attr.renderConfig || {}).label_prefix;
    if (labelPrefix) return `${labelPrefix}${text}`;
    return attr.label ? `${attr.label} ${text}` : text;
  }
  return text;
}

/** The preview's rendered parts, in payload order. */
export function previewParts(entityType, preview, systemSlug = getActiveSystem()) {
  if (!Array.isArray(preview) || !preview.length) return [];
  const family = entityType ? getFamily(entityType, systemSlug) : null;
  const parts = [];
  for (const item of preview) {
    if (!item || item.value === undefined || item.value === null || item.value === '') continue;
    const attr = family && family.attributesByKey ? family.attributesByKey.get(item.key) : null;
    const text = partText(attr, item.value);
    if (text) parts.push(text);
  }
  return parts;
}

/** The one-line preview a suggestion row renders, or '' when there is none. */
export default function previewText(entityType, preview, systemSlug = getActiveSystem()) {
  return previewParts(entityType, preview, systemSlug).join(' · ');
}
